import {
  CreateCdfParams,
  useCdfRawValues,
  useCreateCustomCdf,
} from 'features/dataLayers'
import { selectionToBins } from 'features/fde/binningUtils'
import { FigureSelectionArea } from 'features/fde/regionTypes'
import { inflateDataTo2D } from 'features/indicationTriage/cards/indicationOverview/useCroppedCdfSummaryStats'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import { useRolodexCache } from 'features/rolodex'
import find from 'lodash/find'
import isNil from 'lodash/isNil'
import { useCallback, useId, useMemo } from 'react'
import { GeckoComponentDataFrame, GeckoSource } from 'types'

export function useWindowFrameCdf(
  cdf: GeckoComponentDataFrame | undefined,
  region: FigureSelectionArea | undefined,
  borderWidthBins: number
): { cdf: GeckoComponentDataFrame | null; isLoading: boolean } {
  const selBins =
    cdf && region
      ? selectionToBins(region, cdf.data.binning, cdf.data.geometry)
      : undefined
  const selMinX = selBins ? selBins.minCorner[0] : undefined
  const selMinY = selBins ? selBins.minCorner[1] : undefined
  const selMaxX = selBins ? selBins.maxCorner[0] : undefined
  const selMaxY = selBins ? selBins.maxCorner[1] : undefined

  const windowMinCorner = useMemo(
    () =>
      !isNil(selMinX) && !isNil(selMinY)
        ? [
            Math.max(selMinX - borderWidthBins, 0),
            Math.max(selMinY - borderWidthBins, 0),
          ]
        : undefined,
    [borderWidthBins, selMinX, selMinY]
  )
  const windowMaxCorner = useMemo(
    () =>
      !isNil(selMaxX) && !isNil(selMaxY) && cdf
        ? [
            Math.min(
              selMaxX + borderWidthBins,
              cdf.data.binning.x_bin_count - 1
            ),
            Math.min(
              selMaxY + borderWidthBins,
              cdf.data.binning.y_bin_count - 1
            ),
          ]
        : undefined,
    [borderWidthBins, cdf, selMaxX, selMaxY]
  )

  const { buffer: binnedDataBuffer, isLoading } = useCdfRawValues(cdf)
  const keepDataAtCoord = useCallback(
    (x: number, y: number) => {
      if (
        !windowMinCorner ||
        !windowMaxCorner ||
        isNil(selMaxX) ||
        isNil(selMaxY) ||
        isNil(selMinX) ||
        isNil(selMinY)
      ) {
        return false
      }
      const [windowMinX, windowMinY] = windowMinCorner
      const [windowMaxX, windowMaxY] = windowMaxCorner

      const xBorderFilter =
        (x <= windowMaxX && x > selMaxX) || (x >= windowMinX && x < selMinX)
      const yBorderFilter =
        (y <= windowMaxY && y > selMaxY) || (y >= windowMinY && y < selMinY)

      return (
        (xBorderFilter && y >= windowMinY && y <= windowMaxY) ||
        (yBorderFilter && x >= windowMinX && x <= windowMaxX)
      )
    },
    [selMaxX, selMaxY, selMinX, selMinY, windowMaxCorner, windowMinCorner]
  )
  const frameValues = useMemo(() => {
    if (!cdf) {
      return undefined
    }
    return binnedDataBuffer?.values.map((value, index) => {
      const x = index % cdf.data.binning.x_bin_count
      const y = Math.floor(index / cdf.data.binning.x_bin_count)
      return keepDataAtCoord(x, y) ? value : NaN
    })
  }, [cdf, keepDataAtCoord, binnedDataBuffer])

  const createCdfParams = useMemo<CreateCdfParams | null>(
    () =>
      frameValues && cdf
        ? {
            view: cdf.data.params.view,
            source: GeckoSource.Projection,
            aggregation: cdf.data.params.aggregation,
            measurementUnit: cdf.data.params.measurement_unit,
            anglePlotSetting: cdf.data.params.angle_plot_setting,
            algorithm: cdf.data.params.algorithm,
            tminMethod: cdf.data.params.tmin_method,
            binning: cdf.data.binning,
            geometry: cdf.data.geometry,
            tags: { ...cdf.tags, 'component-data-frame': cdf.id },
            data: async () =>
              frameValues
                ? inflateDataTo2D(
                    Array.from(frameValues),
                    cdf.data.binning.x_bin_count
                  )
                : [],
          }
        : null,
    [cdf, frameValues]
  )
  const cdfId = `${useId()}-cropped`
  const rdCache = useRolodexCache()
  const { isLoading: customCdfLoading, id } = useCreateCustomCdf(
    createCdfParams,
    rdCache,
    cdfId
  )
  const { componentDataFrames } = useIndicationData()
  return useMemo(
    () => ({
      isLoading: isLoading || customCdfLoading,
      cdf: find(componentDataFrames, (cdf) => cdf.id === id) ?? null,
    }),
    [componentDataFrames, customCdfLoading, id, isLoading]
  )
}
